import type { RefObject } from 'react';
import type { ChatMessage, LlmMessage } from '../types';
import { store } from '../state/store';
import type { ChatOrchestrator } from '../services/orchestrator';

/** Rebuild plain-text LLM history from display bubbles when structured history was never seeded. */
export function reconcileHistoryFromDisplayMessages(messages: ChatMessage[]): LlmMessage[] {
  const out: LlmMessage[] = [];
  for (const msg of messages) {
    if (msg.role !== 'user' && msg.role !== 'assistant') continue;
    const text = typeof msg.content === 'string' ? msg.content.trim() : '';
    if (!text || msg.isStreaming) continue;
    out.push({ role: msg.role, content: text });
  }
  return out;
}

export function seedHistoryIfEmpty(): void {
  if (store.getState().historyMessages.length > 0) return;
  const recovered = reconcileHistoryFromDisplayMessages(store.getState().messages);
  if (recovered.length > 0) {
    store.setState({ historyMessages: recovered });
  }
}

export function appendUserToHistory(content: string): void {
  store.setState({
    historyMessages: [...store.getState().historyMessages, { role: 'user', content }],
  });
}

export function appendAssistantToHistory(content: string): void {
  const trimmed = content.trim();
  if (!trimmed) return;
  store.setState({
    historyMessages: [...store.getState().historyMessages, { role: 'assistant', content: trimmed }],
  });
}

export function setStructuredHistory(messages: LlmMessage[]): void {
  store.setState({ historyMessages: messages });
}

const ORCHESTRATOR_WAIT_MS = 5000;
const ORCHESTRATOR_POLL_MS = 50;

export async function waitForOrchestrator(
  ref: RefObject<ChatOrchestrator | null> | null,
  getMcpReadyPromise?: () => Promise<void> | null,
  timeoutMs = ORCHESTRATOR_WAIT_MS,
): Promise<ChatOrchestrator | null> {
  if (ref?.current) return ref.current;

  const readyPromise = getMcpReadyPromise?.();
  if (readyPromise) {
    await Promise.race([
      readyPromise.catch(() => undefined),
      new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
    if (ref?.current) return ref.current;
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (ref?.current) return ref.current;
    await new Promise((resolve) => setTimeout(resolve, ORCHESTRATOR_POLL_MS));
  }

  return ref?.current ?? null;
}
